import { ReactNode } from "react";
import { PencilIcon, TrashIcon } from "lucide-react";
import { Button } from "@/components/UI/Buttons/Button";
import { cn } from "@/lib/utils";

interface PromptListProps<T, P> {
  prompts?: P[];
  selectedType?: T;
  onDeletePrompt: (prompt: P) => void;
  onEditPrompt: (prompt: P) => void;
  title?: string;
  renderPromptContent?: (prompt: P) => ReactNode;
  renderPromptBadges?: (prompt: P) => ReactNode;
}

function PromptList<
  T extends { id: number; name: string },
  P extends { id: number; name: string; content: string },
>({
  prompts,
  selectedType,
  onDeletePrompt,
  onEditPrompt,
  title,
  renderPromptContent,
  renderPromptBadges,
}: PromptListProps<T, P>) {
  return (
    <div className="w-full">
      <h4 className="text-ink mb-4 line-clamp-3 text-center font-semibold">
        {title || "Prompts"} {selectedType && `- ${selectedType.name}`}
      </h4>

      <div className="flex flex-col gap-4">
        {prompts?.map((prompt) => (
          <div
            key={prompt.id}
            className={cn(
              "rounded-xl border border-line-strong bg-paper p-4 transition-colors",
              "hover:bg-panel",
            )}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                {renderPromptContent ? (
                  renderPromptContent(prompt)
                ) : (
                  <>
                    <div className="text-ink line-clamp-1 text-xl font-medium">
                      {prompt.name}
                    </div>
                    <div className="text-muted mt-1 line-clamp-2 text-sm">
                      {prompt.content}
                    </div>
                  </>
                )}
              </div>

              <div className="flex items-center gap-2">
                {renderPromptBadges && renderPromptBadges(prompt)}

                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => onEditPrompt(prompt)}
                >
                  <PencilIcon />
                </Button>

                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => onDeletePrompt(prompt)}
                >
                  <TrashIcon />
                </Button>
              </div>
            </div>
          </div>
        ))}

        {prompts?.length === 0 && (
          <div className="text-muted p-4 text-center">
            Nėra promptų šiam tipui
          </div>
        )}
      </div>
    </div>
  );
}

export default PromptList;
